import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";
import Card from "components/card/Card";
import Checkbox from "components/form/checkbox/Checkbox";
import { IClientPortalBookingState, IClientPortalPaymentState, PaymentStep } from "./ClientPortalBooking";
import { Select } from "components/select/index";
import "./clientPortalBooking.scss";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { ITeeTime } from "redux/reducers/models/teetime";
import ButtonGroup from "components/buttonGroup/ButtonGroup";
import Callout from "components/callout/Callout";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TextField from "components/form/textField/TextField";

interface IProps {
  bookingState: IClientPortalBookingState;
  paymentState: IClientPortalPaymentState;
  handleCardDropDownChange: (value: number, id: string, card: Record<string, any>) => void;
  openNewCardModal: () => void;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePrepaidChange: (key: string | number) => void;
  returnToHomePage: (deleteLock: boolean) => void;
  disableCheckout: () => boolean;
  displayPrePaidWarning: () => void;
  displayBookingFeeWarning: () => void;
  checkFriendsToAdd: () => void;
  displayTerms: (e: React.MouseEvent) => void;
  bookingFeePaid: boolean;
  facilityLongName: string;
  teeTime: ITeeTime;
  showCreditCardDropdown: boolean;
  showPrepaidOptional: boolean;
  totalDueNow: number;
  handleBookingStateInput: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export default function ClientPortalBookingCard(props: IProps) {
  const { t } = useTranslation();
  const {
    bookingState,
    paymentState,
    handleCardDropDownChange,
    openNewCardModal,
    handleCheckboxChange,
    handlePrepaidChange,
    returnToHomePage,
    disableCheckout,
    displayPrePaidWarning,
    displayBookingFeeWarning,
    checkFriendsToAdd,
    displayTerms,
    bookingFeePaid,
    facilityLongName,
    teeTime,
    showCreditCardDropdown,
    showPrepaidOptional,
    totalDueNow,
  } = props;
  const { Option } = Select;
  const windowSize = useWindowSize();
  const [showNote, setShowNote] = useState<boolean>(false);

  return (
    <Card>
      <Card.Section>
        <p className="client-portal-booking-booking-details-selection-title">{"Booking Info"}</p>
        {showPrepaidOptional ? (
          <>
          <ButtonGroup 
            buttons={[{label: "Pay At Course", key: "payAtCourse"}, {label: "Pay Now", key: "payNow"}]} 
            onButtonClick={key => handlePrepaidChange(key)} 
          />
          <br />
          </>
        ) : null}
        {showPrepaidOptional && bookingState?.payAtCourse && bookingState?.cart?.booking_fee_required && !bookingFeePaid ? (
          <><Callout type={"info"} title={t("Booking Fee Payment Required")} content={t("The booking fees will still be charged online. The remaining fees will be charged at the course")} /> <br/></>
        ) : null}
        {showCreditCardDropdown ? (
          <div className="client-portal-booking-dropdown">
            <Select
              label={t("Credit Card")}
              defaultValue={bookingState.selectedCardId}
              onChange={(value: number, card: Record<string, any>) =>
                handleCardDropDownChange(value, "selectedCardId", card)
              }
              disabled={paymentState?.step >= PaymentStep.put_capture && !!paymentState?.order}
            >
              <li className="ui-select-dropdown-list-item" onClick={openNewCardModal}>
                <p className="text-md">{"Add New Card"}</p>
              </li>
              {bookingState.customerCreditCards?.map((card, index) => {
                return (
                  <Option key={index} value={card?.id} name={card?.brand} extraValues={card}>
                    <span>
                      {String(card?.brand)?.charAt(0).toUpperCase()}
                      {String(card?.brand).slice(1)} •••• •••• •••• {card?.last4}
                    </span>
                  </Option>
                );
              })}
            </Select>
          </div>
        ) : null}


        {!showNote ? (
           <Button className="p-0 mt-2 mb-2" type="tertiary" size="medium" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowNote(true)}>{t("Booking Notes")}</Button>
        ) : (
          <>
          <br />
          <TextField id="note" label={t("Note")} value={bookingState?.note} placeholder={t("Booking notes...")} onChange={props.handleBookingStateInput} characterCountBottom maxLength={150}/>
          </>
        )}
       

        

        <div className="client-portal-booking-terms">
          <Checkbox
            id="acceptTerms"
            size="small"
            value={bookingState.acceptTerms}
            checked={bookingState.acceptTerms}
            onChange={handleCheckboxChange}
            borderStyle={{ outline: "1px solid black" }}
            label={
              <span>
                I agree to the{" "}
                <span onClick={e => displayTerms(e)} className="client-portal-booking-conditions">
                  Booking and Payment terms of {facilityLongName ?? ""}
                </span>
              </span>
            }
          />
        </div>
        <div className="client-portal-booking-booking-details-selection-confirm-button-group">
          <Button
            block={windowSize.width <= MOBILE_WIDTH}
            className="w-100"
            size="medium"
            type="default"
            onClick={() => returnToHomePage(true)}
          >
            {t("Back")}
          </Button>
          <Button
            className="w-100"
            size="medium"
            type="primary"
            disabled={disableCheckout()}
            block={windowSize.width <= MOBILE_WIDTH}
            onClick={
              (totalDueNow > 0 && !bookingFeePaid && teeTime?.pre_paid_required)
                ? displayPrePaidWarning
                : totalDueNow > 0 && bookingState?.cart?.total_price_due &&
                  paymentState?.step >= PaymentStep.post_cart &&
                  paymentState?.step <= PaymentStep.put_capture
                ? displayBookingFeeWarning
                : checkFriendsToAdd
            }
          >
            {totalDueNow && !bookingFeePaid ? "Book and Pay" : "Confirm and Book"}
          </Button>
        </div>
      </Card.Section>
    </Card>
  );
}
